import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Plus, Share2 } from 'lucide-react';
import { useEntity } from '@/hooks/useEntity';
import { ENTITY_TYPES, ENTITY_LABEL } from '@/lib/constants/entity-types';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { ROUTES } from '@/lib/constants/routes';
import { PERMISSIONS, type PermissionKey } from '@/lib/constants/permissions';
import { FULL_SCREEN_STATE_MIN_H } from '@/lib/constants/layout';
import { usePermission } from '@/hooks/usePermission';
import { getApiErrorDetail } from '@/lib/utils/error.utils';
import {
  EntityAccessBlockedState,
  blockedVariantForError,
} from '@/features/profiles/components/EntityAccessBlockedState';
import { useTeamMembers } from '@/features/team/hooks/useTeamMembers';
import { useTeamInvites } from '@/features/team/hooks/useTeamInvites';
import { pendingInvitesPollInterval } from '@/features/team/pending-invites-poll';
import { useMemberActions } from '@/features/team/hooks/useMemberActions';
import { useInviteActions } from '@/features/team/hooks/useInviteActions';
import { useRecipients } from '@/features/team/hooks/useRecipients';
import { useRecipientActions } from '@/features/team/hooks/useRecipientActions';
import { TeamMembersTable } from '@/features/team/components/TeamMembersTable';
import { RecipientsTable } from '@/features/team/components/RecipientsTable';
import { InviteMemberDialog } from '@/features/team/components/InviteMemberDialog';
import { ShareProfileDialog } from '@/features/team/components/ShareProfileDialog';
import { getConfirmDialogConfig, type ConfirmState } from './team-moderation-confirm';
import { TEAM_PAGE_FEATURES } from './team-page-features';
import { useDataRoomSeatColumn } from '@/features/team/hooks/useDataRoomSeatColumn';

/**
 * Team Access page (MYN-43), reused across every entity type this
 * route is mounted for (startup, investor) - copy is genericized via
 * `ENTITY_LABEL`/`entity.entityType`, and `team.api.ts` picks the matching API
 * resource. Thin container: it supplies the active entity and wires the team
 * feature's queries/mutations to the presentational tables.
 *
 * Route ENTRY is now identical for both personas (`ProtectedRoute` in
 * App.tsx): neither the startup nor the investor route is gated on
 * `team.manage`/`team.invite` any more - any startup_admin/startup_member or
 * investor_admin/investor_member reaches the page regardless of permission (a
 * member with neither sees the page in a pure read-only "view-only" state -
 * there is no page to be turned away from). Do not re-derive either persona's
 * entry gate here - `ProtectedRoute` is the one source of truth for WHETHER a
 * session reaches this component.
 *
 * This component DOES check permissions itself for an orthogonal question the
 * route can't answer: WHICH VARIANT to render once inside, via `readOnly`
 * (driven by `usePermission(PERMISSIONS.TEAM_MANAGE)`) - this is the only
 * gate left, and it is persona-agnostic. A `team.manage` holder gets the full
 * page; anyone else (a `team.invite`-only holder, or a member with neither
 * permission, for either persona) gets the same roster read-only (see
 * `readOnly` on `TeamMembersTable`) - `team.invite` alone additionally
 * unlocks the "Invite Team Member" button. This is not a regression of "one
 * source of truth for the gate" - it's a second, different decision made once
 * the route (which no longer gates on permission for either persona) has
 * already been passed.
 *
 * "Shared Profile Recipients" is `shares.manage`-only outright (not rendered
 * at all otherwise, not merely disabled): who else can see the shared
 * profile is a distinct capability from team management, delegable to a
 * Startup Member independently of `team.manage` (mirrors the backend's
 * `SHARES_MANAGE`, toggleable the same way `TEAM_INVITE` is) - not something
 * either `entity.profile.edit` or `team.invite` alone imply.
 *
 * Still falls back to the account page when there is no active entity at all.
 */
export function TeamPermissionsPage() {
  const entity = useEntity();
  const entityId = entity?.entityId;
  const entityType = entity?.entityType;
  const canManageTeam = usePermission(PERMISSIONS.TEAM_MANAGE);
  const canInvite = usePermission(PERMISSIONS.TEAM_INVITE);
  const canManageShares = usePermission(PERMISSIONS.SHARES_MANAGE);
  // The data-room access read requires this permission, so it decides
  // whether the seat column can be read at all.
  const canManageBilling = usePermission(PERMISSIONS.BILLING_MANAGE);
  const showDataRoom = (entityType ? TEAM_PAGE_FEATURES[entityType].daxeSeats : false) && canManageBilling;
  const renderDataRoomSeat = useDataRoomSeatColumn(entityId ?? '', showDataRoom);
  // Computed here (not only in the later TEAM_PAGE_FEATURES destructure)
  // because useRecipients below must gate on it too - SHARES_MANAGE is a
  // generic permission every owner/admin holds by RBAC default regardless of
  // entity type, but the Shared Profile Recipients feature itself is
  // startup-only. Gating the fetch on canManageShares alone sent an
  // investor's or trusted partner's entityId to the startup-only
  // /startups/:entityId/shares endpoint, 404ing and taking the whole page
  // down with "Could not load your team" for any owner/admin of either type.
  const showSharing = entityType ? TEAM_PAGE_FEATURES[entityType].sharing : false;

  // Fetched for EVERY member, not gated on `canInvite`. Pending invites are not
  // a separate card: `TeamMembersTable` merges them into the Team Members roster
  // as rows with a Pending badge, and the card's count is members + invites. So
  // withholding them does not hide a section, it silently drops people from the
  // roster and under-reports the count. The Backend listing gate was opened to
  // any member for exactly this reason (see LIST_INVITES_REQUIRES_ANY_OF);
  // creating and revoking stay privileged.
  const invites = useTeamInvites(entityId, { pollWhilePending: true });
  const members = useTeamMembers(entityId, entityType, pendingInvitesPollInterval(invites.data));
  // entityType ?? STARTUP is a defensive placeholder only, never the value
  // actually used: `!entity` redirects away below before render, and
  // useMemberActions' mutations aren't invoked until a member row exists
  // (which implies a resolved entity). Hooks still run unconditionally on
  // this first pass per the rules of hooks, so they need *some* value.
  const memberActions = useMemberActions(entityId ?? '', entityType ?? ENTITY_TYPES.STARTUP);
  const inviteActions = useInviteActions(entityId ?? '');
  // Recipients are `shares.manage`-only AND startup-only (see the class doc
  // comment above and the showSharing note above) - don't fetch for a viewer
  // who won't see the section at all.
  const recipients = useRecipients(showSharing && canManageShares ? entityId : undefined);
  const recipientActions = useRecipientActions(entityId ?? '');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Pending destructive action awaiting confirmation (remove member / revoke invite / revoke recipient).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  if (!entity) return <Navigate to={ROUTES.ACCOUNT} replace />;

  // Genericizes what was startup-only copy ("your startup team" / "Share
  // Startup Profile") so the same page reads correctly for every entity type
  // this route is mounted for (startup, investor).
  const entityLabel = ENTITY_LABEL[entity.entityType];
  const { offerAccess: hasPerks, inviteSector } = TEAM_PAGE_FEATURES[entity.entityType];

  // MYN-280 (FR-195) - a read-only viewer (any entity type this route serves)
  // is pointed at their admin instead of being shown management copy they
  // cannot act on; a team.manage holder keeps today's sharing-aware text.
  const introText = !canManageTeam
    ? `Contact your ${entityLabel} Admin to update your permissions.`
    : showSharing
      ? `Manage your ${entityLabel.toLowerCase()} team, control permissions, and share your ${entityLabel.toLowerCase()} profile with external recipients.`
      : `Manage your ${entityLabel.toLowerCase()} team and control permissions.`;

  const isLoading =
    members.isLoading || invites.isLoading || (showSharing && canManageShares && recipients.isLoading);
  const isError =
    members.isError || (showSharing && canManageShares && recipients.isError);
  // NOTE: `invites.isError` is deliberately NOT fatal. It is a secondary read on
  // a page whose primary content is the member roster, and folding its failure
  // into the page-level error state is what blanked the whole page with
  // "Could not load your team" while /me and /team had both succeeded. If the
  // invites read fails the roster still renders; the pending rows simply do not
  // appear.

  // Team Access is a startup-scoped route too (GET /startups/:entityId/team),
  // so a suspended/deleted startup 403s here exactly like the profile route
  // (MYN-131 AC-4). Keyed off the error CODE via the shared, entity-agnostic
  // `blockedVariantForError` - never off `entity.entityType` - so an investor
  // hitting an unrelated failure still gets the generic message below, and
  // this page needs no persona branching to get it right.
  const blockedVariant = members.isError ? blockedVariantForError(members.error) : undefined;

  // The notice takes over the whole screen rather than rendering as a card
  // under the page chrome: a "Team Access" title bar - and, worse, its
  // "Manage your ... team, control permissions, ..." description - would
  // offer the very surface the notice exists to say is unavailable. Same
  // early return as `ProfileEditor`'s blocked branch, and for the same reason;
  // like it, the notice is returned bare and centers itself against the
  // viewport via `LinkStatusCard`'s `fullScreen` (`AppLayout` gives
  // `<main className="flex-1">` no definite height, so a percentage height
  // here would collapse the card back to the top).
  if (blockedVariant) {
    return <EntityAccessBlockedState variant={blockedVariant} />;
  }

  // Same reason as `ProfileEditor`'s pending branch: blocked-ness is only
  // knowable once the fetch settles, so render no page chrome until then.
  // Showing the "Team Access" title bar and its "Manage your ... team" blurb
  // during the request only to strip both when it 403s is worse than either
  // end state (MYN-131).
  if (isLoading) {
    return (
      <div className={`flex ${FULL_SCREEN_STATE_MIN_H} items-center justify-center`}>
        <LoadingSpinner size="lg" label="Loading team" />
      </div>
    );
  }

  // Surface a failure from any team/invite/recipient mutation (the
  // switch/action reflects server state, so without this a failed
  // toggle/remove would be silent).
  const failedAction = [
    memberActions.togglePermission,
    memberActions.changeRole,
    memberActions.remove,
    inviteActions.revoke,
    recipientActions.remove,
    recipientActions.regenerate,
  ].find((mutation) => mutation.isError);
  const actionError = failedAction
    ? (getApiErrorDetail(failedAction.error) ?? 'That action could not be completed. Please try again.')
    : undefined;

  // Single lookup keyed by `confirm.kind` instead of five parallel ternary
  // chains on the same discriminant (mnr-standards Style Rules) - each kind's
  // copy/pending-state/mutate call lives in one place, not four.
  const confirmDialogConfig = getConfirmDialogConfig(confirm, {
    memberActions,
    inviteActions,
    recipientActions,
    entityLabel,
    onDone: () => setConfirm(null),
  });

  return (
    <>
      <PageHeader title="Team Access" />
      {/* Figma's card content measures 989px. Widened from max-w-6xl (1152px) to
          max-w-7xl (1280px) so the intro text beside the action buttons runs
          wider before wrapping (UX sheet, StartupAdminVF r99). Still fluid below. */}
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6">
        {/* Centred, not top-aligned: top alignment floated the one-line intro
            above the taller buttons and left a larger gap before the card. */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* flex-1 + no max-width: the description uses the row's full width
              beside the action buttons instead of wrapping at ~50% (UX sheet,
              StartupAdminVF r99). */}
          <div className="min-w-0 flex-1">
            {/* No repeated "Team Access" heading here - PageHeader above
                already shows the page title; this row is just the description. */}
            <p className="text-sm leading-relaxed text-subtext">{introText}</p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {/* Sharing (MYN-41/54) is startup-only for now (see
                TEAM_PAGE_FEATURES) and additionally gated on shares.manage -
                a distinct, delegable capability from team.manage.
                A suspended/deleted startup's member never reaches this row at
                all (review finding M-5): the blocked early return above
                replaces the entire page, so there is no Share button left to
                403 on. */}
            {showSharing && canManageShares && (
              <Button
                type="button"
                variant="white"
                className="rounded-xl! inline-flex items-center gap-2 whitespace-nowrap px-6 py-2.5 text-sm"
                onClick={() => setShareOpen(true)}
              >
                <Share2 size={16} aria-hidden="true" />
                Share {entityLabel} Profile
              </Button>
            )}
            {/* Gated on team.invite specifically (not "can reach this page" -
                a team.manage holder without team.invite, however unlikely
                today's seeded roles make it, correctly loses this button too).
                A blocked entity never reaches this row either, same as the
                Share button above. */}
            {canInvite && (
              <Button
                type="button"
                variant="green"
                className="rounded-xl! inline-flex items-center gap-2 whitespace-nowrap px-6 py-2.5 text-sm"
                onClick={() => setInviteOpen(true)}
              >
                <Plus size={16} aria-hidden="true" />
                Invite Team Member
              </Button>
            )}
          </div>
        </div>

        {isError && (
          <ErrorState
            title="Could not load your team"
            description="Please refresh the page to try again."
          />
        )}

        {actionError && (
          <p
            role="alert"
            className="rounded-card border border-danger/30 bg-danger/10 px-6 py-3 text-sm text-danger"
          >
            {actionError}
          </p>
        )}

        {!isError && (
          <>
            <SectionCard title="Team Members">
              <TeamMembersTable
                entityType={entity.entityType}
                hasPerks={hasPerks}
                showSector={inviteSector}
                members={members.data ?? []}
                invites={invites.data ?? []}
                onTogglePermission={(membershipId, permissionKey, isEnabled) =>
                  memberActions.togglePermission.mutate({
                    membershipId,
                    permissionKey: permissionKey as PermissionKey,
                    isEnabled,
                  })
                }
                onChangeRole={(membershipId, role) =>
                  memberActions.changeRole.mutate({ membershipId, role })
                }
                onRemove={(membershipId) => {
                  const member = members.data?.find((m) => m.membershipId === membershipId);
                  setConfirm({ kind: 'remove', membershipId, name: member?.name ?? 'this member' });
                }}
                onRevokeInvite={(inviteId) => {
                  const invite = invites.data?.find((i) => i.id === inviteId);
                  setConfirm({ kind: 'revoke', inviteId, email: invite?.email ?? 'this invite' });
                }}
                readOnly={!canManageTeam}
                renderDataRoomSeat={renderDataRoomSeat}
              />
            </SectionCard>


            {/* Gated on shares.manage specifically (not team.manage), and
                startup-only for now (see TEAM_PAGE_FEATURES): a viewer with
                just entity.profile.edit or team.invite manages their own
                narrow slice, not who else can see the shared profile - so
                this section doesn't exist for them at all, not merely
                disabled. */}
            {showSharing && canManageShares && (
              <SectionCard
                title="Shared Profile Recipients"
                subtitle={`People outside your team who have been sent a link to view your ${entityLabel.toLowerCase()} profile.`}
                headerRight={
                  <Badge variant="green-solid" size="count">{recipients.data?.length ?? 0}</Badge>
                }
              >
                <RecipientsTable
                  recipients={recipients.data ?? []}
                  onRevoke={(recipientId) => {
                    const recipient = recipients.data?.find((r) => r.id === recipientId);
                    setConfirm({
                      kind: 'revoke-recipient',
                      recipientId,
                      email: recipient?.recipientEmail ?? 'this recipient',
                    });
                  }}
                  onRegenerate={(recipientId) => {
                    const recipient = recipients.data?.find((r) => r.id === recipientId);
                    setConfirm({
                      kind: 'regenerate-recipient',
                      recipientId,
                      email: recipient?.recipientEmail ?? 'this recipient',
                    });
                  }}
                  isRevoking={(recipientId) =>
                    recipientActions.remove.isPending &&
                    recipientActions.remove.variables === recipientId
                  }
                  isRegenerating={(recipientId) =>
                    recipientActions.regenerate.isPending &&
                    recipientActions.regenerate.variables === recipientId
                  }
                />
              </SectionCard>
            )}
          </>
        )}
      </div>

      <InviteMemberDialog
        entityId={entity.entityId}
        showSector={inviteSector}
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />

      <ShareProfileDialog
        entityId={entity.entityId}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />

      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(next) => {
          if (!next) setConfirm(null);
        }}
        title={confirmDialogConfig?.title ?? ''}
        description={confirmDialogConfig?.description}
        confirmLabel={confirmDialogConfig?.confirmLabel ?? ''}
        isConfirming={confirmDialogConfig?.isConfirming ?? false}
        onConfirm={() => confirmDialogConfig?.onConfirm()}
      />
    </>
  );
}

